import React, { useState } from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import { Card, CardContent } from "./Card";
import { useTheme } from "../theme/useTheme";
import PropTypes from "prop-types";

/**
 * Componente MetricCard para mostrar métricas: título arriba (en negrita) y,
 * debajo, un icono opcional junto al valor a 30px. Layout compartido por
 * Dashboard, Team y las tarjetas de KPI de Clientes.
 */
export const MetricCard = ({
  icon: IconComponent,
  iconColor,
  iconBackgroundColor,
  value,
  label,
  isLoading = false,
  isSpecial = false,
  specialTextColor,
  specialBackgroundGradient,
  specialBorderColor,
  onClick,
  valueAdornment = null,
  footer = null,
  contentSx = {},
  ...props
}) => {
  const { theme, colors, neutrals } = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  if (isLoading) {
    return (
      <Card
        sx={{
          height: "100%",
        }}
      >
        <CardContent
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            height: "100%",
          }}
        >
          <Skeleton variant="text" width="55%" height={20} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            {IconComponent && (
              <Skeleton
                variant="rounded"
                width={40}
                height={40}
                sx={{ borderRadius: 1 }}
              />
            )}
            <Skeleton variant="text" width={70} height={40} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (isSpecial) {
    return (
      <Box
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-label={onClick ? `${label}` : undefined}
        sx={{
          cursor: onClick ? "pointer" : "default",
          border: `1px solid ${
            specialBorderColor || `${colors.primary.light}55`
          }`,
          background:
            specialBackgroundGradient ||
            `linear-gradient(135deg, ${colors.primary.light}11, ${neutrals.gray[50]})`,
          borderRadius: "12px",
          boxShadow: theme.shadows[1],
          "&:hover": onClick
            ? {
                boxShadow: theme.shadows[2],
              }
            : {},
          ...props.sx,
        }}
        {...props}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            minHeight: "80px",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: theme.palette.text.secondary }}
          >
            {label}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            {IconComponent && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: iconBackgroundColor,
                  display: "flex",
                }}
              >
                <IconComponent size={24} color={iconColor} />
              </Box>
            )}
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "30px",
                lineHeight: 1.2,
                color: specialTextColor || colors.primary.main,
              }}
            >
              {value}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={onClick ? `${label}` : undefined}
      sx={{
        cursor: onClick ? "pointer" : "default",
        ...props.sx,
      }}
      {...props}
    >
      <Card sx={{ height: "100%" }}>
        <CardContent
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            height: "100%",
            ...contentSx,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: theme.palette.text.secondary }}
          >
            {label}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: valueAdornment ? "wrap" : undefined }}>
            {IconComponent && (
              <Box
                sx={{
                  p: 1.25,
                  backgroundColor: iconBackgroundColor,
                  borderRadius: 1,
                  display: "flex",
                }}
              >
                <IconComponent size={22} color={iconColor} />
              </Box>
            )}
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "30px",
                lineHeight: 1.2,
                color: theme.palette.text.primary,
              }}
            >
              {value}
            </Typography>
            {valueAdornment}
          </Box>
          {footer}
        </CardContent>
      </Card>
    </Box>
  );
};

MetricCard.propTypes = {
  icon: PropTypes.elementType,
  iconColor: PropTypes.string,
  iconBackgroundColor: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  isSpecial: PropTypes.bool,
  specialTextColor: PropTypes.string,
  specialBackgroundGradient: PropTypes.string,
  specialBorderColor: PropTypes.string,
  onClick: PropTypes.func,
  // Optional node next to the value (e.g. a TrendDelta) and below it.
  valueAdornment: PropTypes.node,
  footer: PropTypes.node,
  // Extra styles for the card's body, e.g. centring it when the card has to
  // fill a taller row than its content needs.
  contentSx: PropTypes.object,
};

/** Small "+ N archivadas" next to a count that excludes archived agencies. */
export const ArchivedCount = ({ count }) =>
  count ? (
    <Typography variant="body2" sx={{ color: "text.secondary", ml: 1 }}>
      + {count} {count === 1 ? "archivada" : "archivadas"}
    </Typography>
  ) : null;

ArchivedCount.propTypes = {
  count: PropTypes.number,
};
